import React, { useState } from 'react';
import Form from '@/components/common/form/Form';

import { useAppDispatch, useAppSelector } from '@/redux';
import TextFieldV2 from '@/components/common/form/fields/TextFieldV2';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import { CallToActionKind } from '@/types';

import styles from './DiscountForm.module.scss';
import { selectDiscountCheckStatus, selectDiscountFormErrorCode } from '@/redux/slices/checkout.slice';
import { validateDiscountCode } from '@/redux/thunks/checkout/validateDiscountCode';
import Icon from '@/components/common/iconedLinks/Icon';
import { hasOnlyLatinChars } from '@/utils/formUtils';
import { ERROR_TYPE } from '@/constants/form';
import { URLS } from '@/constants/urls';

export const DiscountForm = ({ t, initialCode = '', isReadonly = false }) => {
  const dispatch = useAppDispatch();
  const [code, setCode] = useState(initialCode);
  const discountCodeStatus = useAppSelector(selectDiscountCheckStatus);
  const discoundCodeErrorTranslation = useAppSelector(selectDiscountFormErrorCode);

  const onValidateCode = () => {
    if (code) {
      dispatch(validateDiscountCode({ discountCode: code }));
    }
  };

  const onDeleteCode = () => {
    dispatch(validateDiscountCode({ discountCode: '' }));
    setCode('');
  };

  const classNames = [
    styles.form,
    discountCodeStatus === 'errored' ? styles.errored : '',
    discountCodeStatus === 'validated' ? styles.validated : '',
    isReadonly ? styles.readonly : ''
  ].join(' ');

  const subContainerClassNames = [
    styles['sub-container'],
    discountCodeStatus === 'pending' ? styles.pending : '',
  ].join(' ');

  const nodeBasedOnDiscountCodeStatus = (() => {
    switch (discountCodeStatus) {
      case 'validated':
        return (
          <div className={classNames}>
            <Icon src={URLS.CHECKED_CIRCLE_GREEN} alt={t('alter:checkMarkIcon')} width={24} height={24} className={styles.icon} />
            <p className={styles.valid}>{code}</p>
            {!isReadonly && (
              <CallToActionContainer
                kind={CallToActionKind.TERTIARY} onClick={onDeleteCode} className={styles.button} type="submit"
                icon={{ url: URLS.CROSS_BLACK, alt: 'close', width: 10, height: 10, rightSide: true }}
              />
            )}
          </div>
        );
      case 'pending':
        return (
          <div className={classNames}>
            <TextFieldV2
              id="ti-discount-code"
              name="discount-code"
              label={t('checkout:cart.discountCodePlaceholder')}
              validators={[ERROR_TYPE.ONLY_LATIN_CHARS]}
              onChange={(e) => setCode(e.target.value)}
              value={code}
            />
            <CallToActionContainer
              onClick={onValidateCode}
              kind={CallToActionKind.SECONDARY}
              type="submit" className={styles.button}
            >
              {t('btn.apply')}
            </CallToActionContainer>
          </div>
        );
      case 'idle':
      case 'errored':
        return (
          <div className={classNames}>
            <TextFieldV2
              id="ti-discount-code"
              name="discount-code"
              label={t('checkout:cart.discountCodePlaceholder')}
              validators={[ERROR_TYPE.ONLY_LATIN_CHARS]}
              onChange={(e) => setCode(e.target.value)}
              defaultValue={code}
            />
            <CallToActionContainer
              onClick={onValidateCode}
              kind={CallToActionKind.SECONDARY}
              type="submit" className={styles.button}
              disabled={typeof hasOnlyLatinChars(code) === 'string'}
            >
              {t('btn.apply')}
            </CallToActionContainer>
            {discountCodeStatus === 'errored' && (
              <p className={styles.error}>{t(`common:errors.errorCodeMessage.${discoundCodeErrorTranslation}`)}</p>
            )}
          </div>
        );
    }
  })();

  return (
    <Form onSubmit={onValidateCode} isReset={!code || code === ''} mode="onChange">
      <div className={styles.container}>
        {discountCodeStatus === 'pending' && (
          <div className={styles.loader} />
        )}
        <div className={subContainerClassNames}>
          {!isReadonly && (
            <label htmlFor="discount-code" className={styles.label}>{t('checkout:cart.discountCodeTitle')}</label>
          )}
          {nodeBasedOnDiscountCodeStatus}
        </div>
      </div>
    </Form>
  );
};
